// Mock Data for Lighthouse Jobs Platform

export interface Company {
  id: number;
  name: string;
  industry: string;
  status: 'verified' | 'pending' | 'suspended';
  joined: string;
  activeJobs: number;
  totalJobs: number;
  subscription: 'Basic' | 'Standard' | 'Premium' | 'Enterprise';
  employees: string;
  location: string;
  description: string;
  website?: string;
  logo?: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  type: 'Candidate' | 'Employer';
  status: 'active' | 'pending' | 'suspended';
  joinDate: string;
  location: string;
  companyId?: number; // For employers
  title?: string;
  skills?: string[];
  experience?: string;
  education?: string;
  bio?: string;
  phone?: string;
}

export interface Job {
  id: number;
  title: string;
  companyId: number;
  location: string;
  salary: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Temporary';
  posted: string;
  status: 'active' | 'closed' | 'flagged';
  description: string;
  requirements: string[];
  benefits: string[];
  applicants: number;
  views: number;
  reports: number;
  matchScore?: number;
  verified: boolean;
  urgent?: boolean;
  remote?: boolean;
  category: string;
  skillTestId?: number; // ID of required pre-interview skill test
  returnerFriendly?: boolean; // Flag for returner-friendly positions
  occupationId?: string;
  occupationTitle?: string;
}

export interface Message {
  id: number;
  conversationId: number;
  senderId: number;
  receiverId: number;
  content: string;
  timestamp: string;
  read: boolean;
}

export interface Conversation {
  id: number;
  candidateId: number;
  employerId: number;
  jobId: number;
  lastMessage: string;
  lastMessageTime: string;
  unread: number;
  status: 'active' | 'archived';
}

// COMPANIES DATA (40 companies across various sectors)
export const companies: Company[] = [
  // Furniture Manufacturing
  {
    id: 1,
    name: 'Heritage Oak Furniture Ltd',
    industry: 'Furniture Manufacturing',
    status: 'verified',
    joined: '2024-03-15',
    activeJobs: 5,
    totalJobs: 23,
    subscription: 'Premium',
    employees: '100-500',
    location: 'High Wycombe, UK',
    description: 'Traditional furniture manufacturer with 50 years of excellence',
    website: 'heritageoak.co.uk'
  },
  {
    id: 2,
    name: 'Modern Living Designs',
    industry: 'Furniture Manufacturing',
    status: 'verified',
    joined: '2024-06-20',
    activeJobs: 3,
    totalJobs: 12,
    subscription: 'Standard',
    employees: '50-100',
    location: 'Birmingham, UK',
    description: 'Contemporary furniture design and manufacturing',
    website: 'modernliving.co.uk'
  },
  {
    id: 3,
    name: 'Comfort Seating Solutions',
    industry: 'Furniture Manufacturing',
    status: 'verified',
    joined: '2024-01-10',
    activeJobs: 4,
    totalJobs: 18,
    subscription: 'Premium',
    employees: '200-500',
    location: 'Manchester, UK',
    description: 'Specialist ergonomic office furniture manufacturer'
  },
  // Engineering
  {
    id: 4,
    name: 'PrecisionTech Engineering',
    industry: 'Engineering',
    status: 'verified',
    joined: '2023-11-05',
    activeJobs: 8,
    totalJobs: 45,
    subscription: 'Enterprise',
    employees: '500-1000',
    location: 'Sheffield, UK',
    description: 'Advanced precision engineering and manufacturing',
    website: 'precisiontech.co.uk'
  },
  {
    id: 5,
    name: 'Industrial Systems Ltd',
    industry: 'Engineering',
    status: 'verified',
    joined: '2024-02-14',
    activeJobs: 6,
    totalJobs: 34,
    subscription: 'Premium',
    employees: '200-500',
    location: 'Leeds, UK',
    description: 'Industrial automation and control systems'
  },
  {
    id: 6,
    name: 'Structural Design Group',
    industry: 'Engineering',
    status: 'verified',
    joined: '2024-04-22',
    activeJobs: 5,
    totalJobs: 28,
    subscription: 'Standard',
    employees: '100-200',
    location: 'Bristol, UK',
    description: 'Civil and structural engineering consultancy'
  },
  {
    id: 7,
    name: 'GreenPower Engineering',
    industry: 'Engineering',
    status: 'verified',
    joined: '2024-05-18',
    activeJobs: 7,
    totalJobs: 22,
    subscription: 'Premium',
    employees: '150-300',
    location: 'Cambridge, UK',
    description: 'Renewable energy engineering solutions'
  },
  // Mechanics & Automotive
  {
    id: 8,
    name: 'AutoCare Service Centre',
    industry: 'Automotive',
    status: 'verified',
    joined: '2024-01-20',
    activeJobs: 4,
    totalJobs: 16,
    subscription: 'Standard',
    employees: '20-50',
    location: 'Milton Keynes, UK',
    description: 'Full-service automotive repair and maintenance'
  },
  {
    id: 9,
    name: 'Fleet Solutions Ltd',
    industry: 'Automotive',
    status: 'verified',
    joined: '2024-03-08',
    activeJobs: 6,
    totalJobs: 24,
    subscription: 'Premium',
    employees: '100-200',
    location: 'Coventry, UK',
    description: 'Commercial fleet maintenance and management'
  },
  {
    id: 10,
    name: 'Performance Motors',
    industry: 'Automotive',
    status: 'verified',
    joined: '2023-12-12',
    activeJobs: 3,
    totalJobs: 14,
    subscription: 'Standard',
    employees: '10-20',
    location: 'Oxford, UK',
    description: 'Specialist performance vehicle tuning and repairs'
  },
  // IT & Technology
  {
    id: 11,
    name: 'CloudFirst Technologies',
    industry: 'Technology',
    status: 'verified',
    joined: '2024-04-15',
    activeJobs: 12,
    totalJobs: 38,
    subscription: 'Enterprise',
    employees: '200-500',
    location: 'London, UK',
    description: 'Cloud computing and infrastructure solutions',
    website: 'cloudfirst.tech'
  },
  {
    id: 12,
    name: 'DataStream Analytics',
    industry: 'Technology',
    status: 'verified',
    joined: '2024-05-22',
    activeJobs: 8,
    totalJobs: 26,
    subscription: 'Premium',
    employees: '100-200',
    location: 'Edinburgh, UK',
    description: 'Big data analytics and business intelligence'
  },
  {
    id: 13,
    name: 'DevBridge Solutions',
    industry: 'Technology',
    status: 'verified',
    joined: '2024-02-28',
    activeJobs: 10,
    totalJobs: 32,
    subscription: 'Premium',
    employees: '150-300',
    location: 'Manchester, UK',
    description: 'Custom software development and consulting'
  },
  {
    id: 14,
    name: 'CyberGuard Security',
    industry: 'Technology',
    status: 'verified',
    joined: '2024-06-10',
    activeJobs: 6,
    totalJobs: 18,
    subscription: 'Premium',
    employees: '50-100',
    location: 'Reading, UK',
    description: 'Cybersecurity solutions and managed services'
  },
  {
    id: 15,
    name: 'AppInnovate Ltd',
    industry: 'Technology',
    status: 'pending',
    joined: '2024-10-15',
    activeJobs: 4,
    totalJobs: 4,
    subscription: 'Standard',
    employees: '20-50',
    location: 'Brighton, UK',
    description: 'Mobile app development studio'
  },
  // Delivery & Logistics
  {
    id: 16,
    name: 'SwiftLogistics UK',
    industry: 'Logistics',
    status: 'verified',
    joined: '2024-01-08',
    activeJobs: 15,
    totalJobs: 68,
    subscription: 'Enterprise',
    employees: '1000+',
    location: 'Birmingham, UK',
    description: 'National courier and logistics services',
    website: 'swiftlogistics.co.uk'
  },
  {
    id: 17,
    name: 'LastMile Delivery Services',
    industry: 'Logistics',
    status: 'verified',
    joined: '2024-03-25',
    activeJobs: 8,
    totalJobs: 42,
    subscription: 'Premium',
    employees: '500-1000',
    location: 'Leeds, UK',
    description: 'Final mile delivery solutions'
  },
  {
    id: 18,
    name: 'GreenFleet Couriers',
    industry: 'Logistics',
    status: 'verified',
    joined: '2024-04-12',
    activeJobs: 6,
    totalJobs: 28,
    subscription: 'Standard',
    employees: '100-200',
    location: 'Bristol, UK',
    description: 'Eco-friendly delivery services'
  },
  // Healthcare
  {
    id: 19,
    name: 'NHS Trust Hertfordshire',
    industry: 'Healthcare',
    status: 'verified',
    joined: '2023-09-01',
    activeJobs: 24,
    totalJobs: 156,
    subscription: 'Enterprise',
    employees: '1000+',
    location: 'Stevenage, UK',
    description: 'National Health Service provider'
  },
  {
    id: 20,
    name: 'Caring Hands Home Care',
    industry: 'Healthcare',
    status: 'verified',
    joined: '2024-02-18',
    activeJobs: 12,
    totalJobs: 45,
    subscription: 'Premium',
    employees: '200-500',
    location: 'Cambridge, UK',
    description: 'Community care and support services'
  },
  {
    id: 21,
    name: 'Wellness Medical Centre',
    industry: 'Healthcare',
    status: 'verified',
    joined: '2024-05-08',
    activeJobs: 8,
    totalJobs: 22,
    subscription: 'Standard',
    employees: '50-100',
    location: 'Norwich, UK',
    description: 'Private healthcare and medical services'
  },
  // Retail
  {
    id: 22,
    name: 'FreshGrocer Supermarkets',
    industry: 'Retail',
    status: 'verified',
    joined: '2024-01-15',
    activeJobs: 18,
    totalJobs: 89,
    subscription: 'Enterprise',
    employees: '1000+',
    location: 'London, UK',
    description: 'UK-wide supermarket chain'
  },
  {
    id: 23,
    name: 'TechHub Electronics',
    industry: 'Retail',
    status: 'verified',
    joined: '2024-03-22',
    activeJobs: 9,
    totalJobs: 34,
    subscription: 'Premium',
    employees: '200-500',
    location: 'Manchester, UK',
    description: 'Consumer electronics retail chain'
  },
  {
    id: 24,
    name: 'Fashion Forward Boutique',
    industry: 'Retail',
    status: 'verified',
    joined: '2024-06-05',
    activeJobs: 5,
    totalJobs: 16,
    subscription: 'Standard',
    employees: '20-50',
    location: 'Bath, UK',
    description: 'Contemporary fashion and accessories'
  },
  // Construction
  {
    id: 25,
    name: 'BuildRight Construction',
    industry: 'Construction',
    status: 'verified',
    joined: '2024-02-10',
    activeJobs: 14,
    totalJobs: 52,
    subscription: 'Premium',
    employees: '500-1000',
    location: 'Milton Keynes, UK',
    description: 'Commercial and residential construction'
  },
  {
    id: 26,
    name: 'Heritage Renovations',
    industry: 'Construction',
    status: 'verified',
    joined: '2024-04-18',
    activeJobs: 6,
    totalJobs: 24,
    subscription: 'Standard',
    employees: '50-100',
    location: 'York, UK',
    description: 'Specialist historic building restoration'
  },
  // Hospitality
  {
    id: 27,
    name: 'Grand Plaza Hotels',
    industry: 'Hospitality',
    status: 'verified',
    joined: '2024-01-22',
    activeJobs: 16,
    totalJobs: 72,
    subscription: 'Premium',
    employees: '1000+',
    location: 'London, UK',
    description: 'Luxury hotel chain'
  },
  {
    id: 28,
    name: 'Countryside Inn & Suites',
    industry: 'Hospitality',
    status: 'verified',
    joined: '2024-05-14',
    activeJobs: 7,
    totalJobs: 28,
    subscription: 'Standard',
    employees: '100-200',
    location: 'Lake District, UK',
    description: 'Boutique hotel and restaurant group'
  },
  // Education
  {
    id: 29,
    name: 'BrightFuture Academy',
    industry: 'Education',
    status: 'verified',
    joined: '2024-03-08',
    activeJobs: 10,
    totalJobs: 38,
    subscription: 'Premium',
    employees: '200-500',
    location: 'Oxford, UK',
    description: 'Independent school and education provider'
  },
  {
    id: 30,
    name: 'SkillsBridge Training',
    industry: 'Education',
    status: 'verified',
    joined: '2024-06-12',
    activeJobs: 5,
    totalJobs: 14,
    subscription: 'Standard',
    employees: '20-50',
    location: 'Birmingham, UK',
    description: 'Vocational training and apprenticeships'
  },
  // Manufacturing
  {
    id: 31,
    name: 'Precision Components Ltd',
    industry: 'Manufacturing',
    status: 'verified',
    joined: '2024-01-28',
    activeJobs: 8,
    totalJobs: 36,
    subscription: 'Premium',
    employees: '200-500',
    location: 'Derby, UK',
    description: 'Aerospace component manufacturing'
  },
  {
    id: 32,
    name: 'PackTech Industries',
    industry: 'Manufacturing',
    status: 'verified',
    joined: '2024-04-05',
    activeJobs: 6,
    totalJobs: 22,
    subscription: 'Standard',
    employees: '100-200',
    location: 'Nottingham, UK',
    description: 'Packaging solutions and manufacturing'
  },
  // Finance
  {
    id: 33,
    name: 'Sterling Financial Services',
    industry: 'Finance',
    status: 'verified',
    joined: '2024-02-20',
    activeJobs: 9,
    totalJobs: 32,
    subscription: 'Premium',
    employees: '500-1000',
    location: 'London, UK',
    description: 'Investment and wealth management'
  },
  {
    id: 34,
    name: 'QuickBooks Accounting',
    industry: 'Finance',
    status: 'verified',
    joined: '2024-05-25',
    activeJobs: 4,
    totalJobs: 16,
    subscription: 'Standard',
    employees: '50-100',
    location: 'Leeds, UK',
    description: 'Accounting and bookkeeping services'
  },
  // Additional companies
  {
    id: 35,
    name: 'CleanSweep Commercial',
    industry: 'Facilities Management',
    status: 'verified',
    joined: '2024-03-12',
    activeJobs: 10,
    totalJobs: 48,
    subscription: 'Standard',
    employees: '200-500',
    location: 'Liverpool, UK',
    description: 'Commercial cleaning and facilities services'
  },
  {
    id: 36,
    name: 'MediaWorks Creative Agency',
    industry: 'Marketing',
    status: 'verified',
    joined: '2024-04-28',
    activeJobs: 5,
    totalJobs: 18,
    subscription: 'Premium',
    employees: '50-100',
    location: 'London, UK',
    description: 'Digital marketing and creative services'
  },
  {
    id: 37,
    name: 'LegalWise Solicitors',
    industry: 'Legal',
    status: 'verified',
    joined: '2024-02-08',
    activeJobs: 6,
    totalJobs: 22,
    subscription: 'Premium',
    employees: '100-200',
    location: 'Manchester, UK',
    description: 'Full-service law firm'
  },
  {
    id: 38,
    name: 'StartupHub Incubator',
    industry: 'Business Services',
    status: 'pending',
    joined: '2024-10-18',
    activeJobs: 3,
    totalJobs: 3,
    subscription: 'Basic',
    employees: '10-20',
    location: 'Cambridge, UK',
    description: 'Startup accelerator and business support'
  },
  {
    id: 39,
    name: 'PropertyPro Real Estate',
    industry: 'Real Estate',
    status: 'verified',
    joined: '2024-05-02',
    activeJobs: 7,
    totalJobs: 26,
    subscription: 'Standard',
    employees: '50-100',
    location: 'Bristol, UK',
    description: 'Residential and commercial property services'
  },
  {
    id: 40,
    name: 'FitLife Gym & Wellness',
    industry: 'Health & Fitness',
    status: 'verified',
    joined: '2024-06-20',
    activeJobs: 8,
    totalJobs: 24,
    subscription: 'Standard',
    employees: '100-200',
    location: 'Southampton, UK',
    description: 'Fitness centers and wellness programs'
  },
];

// USERS DATA (80 users - mix of candidates and employers)
export const users: User[] = [
  // Employers
  {
    id: 1,
    name: 'Sarah Mitchell',
    email: 'sarah.mitchell@heritageoak.co.uk',
    type: 'Employer',
    status: 'active',
    joinDate: '2024-03-15',
    location: 'High Wycombe, UK',
    companyId: 1,
    title: 'HR Manager',
    phone: '+44 7700 900001'
  },
  {
    id: 2,
    name: 'James Patterson',
    email: 'james.p@precisiontech.co.uk',
    type: 'Employer',
    status: 'active',
    joinDate: '2023-11-05',
    location: 'Sheffield, UK',
    companyId: 4,
    title: 'Recruitment Director',
    phone: '+44 7700 900002'
  },
  {
    id: 3,
    name: 'Emma Thompson',
    email: 'e.thompson@cloudfirst.tech',
    type: 'Employer',
    status: 'active',
    joinDate: '2024-04-15',
    location: 'London, UK',
    companyId: 11,
    title: 'Talent Acquisition Lead',
    phone: '+44 7700 900003'
  },
  {
    id: 4,
    name: 'Michael Chen',
    email: 'mchen@swiftlogistics.co.uk',
    type: 'Employer',
    status: 'active',
    joinDate: '2024-01-08',
    location: 'Birmingham, UK',
    companyId: 16,
    title: 'Operations Manager',
    phone: '+44 7700 900004'
  },
  {
    id: 5,
    name: 'Rebecca Williams',
    email: 'r.williams@nhsherts.nhs.uk',
    type: 'Employer',
    status: 'active',
    joinDate: '2023-09-01',
    location: 'Stevenage, UK',
    companyId: 19,
    title: 'Senior HR Officer',
    phone: '+44 7700 900005'
  },
  // Candidates - Engineering/Technical
  {
    id: 6,
    name: 'David Anderson',
    email: 'david.anderson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-10',
    location: 'Sheffield, UK',
    title: 'Senior Mechanical Engineer',
    skills: ['CAD Design', 'SolidWorks', 'Manufacturing', 'Project Management', 'Quality Control'],
    experience: '8 years',
    education: 'BEng Mechanical Engineering',
    bio: 'Experienced mechanical engineer with expertise in precision manufacturing',
    phone: '+44 7700 900101'
  },
  {
    id: 7,
    name: 'Sophie Baker',
    email: 'sophie.baker@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-15',
    location: 'Leeds, UK',
    title: 'Electrical Engineer',
    skills: ['Electrical Systems', 'AutoCAD', 'PLC Programming', 'Circuit Design'],
    experience: '5 years',
    education: 'MEng Electrical Engineering',
    bio: 'Specialized in industrial automation and control systems',
    phone: '+44 7700 900102'
  },
  {
    id: 8,
    name: 'Thomas Wright',
    email: 'thomas.wright@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-20',
    location: 'Bristol, UK',
    title: 'Civil Engineer',
    skills: ['Structural Design', 'Revit', 'Site Management', 'Health & Safety'],
    experience: '6 years',
    education: 'BEng Civil Engineering',
    bio: 'Civil engineer focused on sustainable infrastructure projects',
    phone: '+44 7700 900103'
  },
  // Candidates - IT/Software
  {
    id: 9,
    name: 'Rachel Green',
    email: 'rachel.green@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-05',
    location: 'London, UK',
    title: 'Full Stack Developer',
    skills: ['React', 'Node.js', 'TypeScript', 'AWS', 'MongoDB'],
    experience: '4 years',
    education: 'BSc Computer Science',
    bio: 'Passionate about creating scalable web applications',
    phone: '+44 7700 900104'
  },
  {
    id: 10,
    name: 'Alex Turner',
    email: 'alex.turner@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-12',
    location: 'Manchester, UK',
    title: 'DevOps Engineer',
    skills: ['Docker', 'Kubernetes', 'CI/CD', 'AWS', 'Terraform'],
    experience: '5 years',
    education: 'BSc Software Engineering',
    bio: 'DevOps specialist with cloud infrastructure expertise',
    phone: '+44 7700 900105'
  },
  {
    id: 11,
    name: 'Jennifer Lopez',
    email: 'jennifer.lopez@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-28',
    location: 'Edinburgh, UK',
    title: 'Data Analyst',
    skills: ['Python', 'SQL', 'Power BI', 'Machine Learning', 'Statistics'],
    experience: '3 years',
    education: 'MSc Data Science',
    bio: 'Data analyst specializing in business intelligence',
    phone: '+44 7700 900106'
  },
  {
    id: 12,
    name: 'Marcus Johnson',
    email: 'marcus.j@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-15',
    location: 'Reading, UK',
    title: 'Cybersecurity Analyst',
    skills: ['Network Security', 'Penetration Testing', 'SIEM', 'Incident Response'],
    experience: '4 years',
    education: 'BSc Cybersecurity',
    bio: 'Security professional focused on threat detection and prevention',
    phone: '+44 7700 900107'
  },
  // Candidates - Healthcare
  {
    id: 13,
    name: 'Emily Roberts',
    email: 'emily.roberts@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-15',
    location: 'Stevenage, UK',
    title: 'Senior Care Assistant',
    skills: ['Patient Care', 'First Aid', 'Medication Management', 'Care Planning'],
    experience: '7 years',
    education: 'NVQ Level 3 Health & Social Care',
    bio: 'Dedicated care professional with extensive experience in residential care',
    phone: '+44 7700 900108'
  },
  {
    id: 14,
    name: 'Daniel Harris',
    email: 'daniel.harris@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-22',
    location: 'Cambridge, UK',
    title: 'Healthcare Support Worker',
    skills: ['Patient Support', 'Basic Life Support', 'Personal Care', 'Documentation'],
    experience: '3 years',
    education: 'Care Certificate',
    bio: 'Compassionate healthcare worker committed to quality patient care',
    phone: '+44 7700 900109'
  },
  {
    id: 15,
    name: 'Sarah Collins',
    email: 'sarah.collins@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-08',
    location: 'Norwich, UK',
    title: 'Registered Nurse',
    skills: ['Clinical Care', 'Patient Assessment', 'Emergency Care', 'Team Leadership'],
    experience: '5 years',
    education: 'BSc Nursing',
    bio: 'Registered nurse with A&E and ward experience',
    phone: '+44 7700 900110'
  },
  // Candidates - Logistics/Delivery
  {
    id: 16,
    name: 'Robert Wilson',
    email: 'robert.wilson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-10',
    location: 'Birmingham, UK',
    title: 'Delivery Driver',
    skills: ['Driving License', 'Route Planning', 'Customer Service', 'Time Management'],
    experience: '4 years',
    education: 'Full UK Driving License',
    bio: 'Reliable delivery driver with excellent safety record',
    phone: '+44 7700 900111'
  },
  {
    id: 17,
    name: 'Lisa Brown',
    email: 'lisa.brown@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-18',
    location: 'Leeds, UK',
    title: 'Warehouse Supervisor',
    skills: ['Inventory Management', 'Team Leadership', 'Health & Safety', 'Forklift License'],
    experience: '6 years',
    education: 'IOSH Managing Safely',
    bio: 'Experienced warehouse supervisor with strong leadership skills',
    phone: '+44 7700 900112'
  },
  {
    id: 18,
    name: 'Kevin Martinez',
    email: 'kevin.martinez@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-28',
    location: 'Bristol, UK',
    title: 'Logistics Coordinator',
    skills: ['Supply Chain', 'Route Optimization', 'Inventory Control', 'ERP Systems'],
    experience: '5 years',
    education: 'BSc Logistics & Supply Chain',
    bio: 'Logistics professional specializing in last-mile delivery',
    phone: '+44 7700 900113'
  },
  // Candidates - Automotive/Mechanics
  {
    id: 19,
    name: 'Peter Davies',
    email: 'peter.davies@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-15',
    location: 'Milton Keynes, UK',
    title: 'Automotive Technician',
    skills: ['Vehicle Diagnostics', 'Engine Repair', 'MOT Testing', 'Customer Service'],
    experience: '8 years',
    education: 'Level 3 Automotive Technician',
    bio: 'Qualified technician with expertise in diagnostics and repairs',
    phone: '+44 7700 900114'
  },
  {
    id: 20,
    name: 'Andrew Scott',
    email: 'andrew.scott@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-22',
    location: 'Coventry, UK',
    title: 'Fleet Mechanic',
    skills: ['Heavy Vehicle Repair', 'Preventive Maintenance', 'Hydraulics', 'Electrical Systems'],
    experience: '6 years',
    education: 'HGV Maintenance Certificate',
    bio: 'Experienced in commercial vehicle maintenance and repair',
    phone: '+44 7700 900115'
  },
  // Candidates - Furniture/Manufacturing
  {
    id: 21,
    name: 'Oliver Smith',
    email: 'oliver.smith@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-25',
    location: 'High Wycombe, UK',
    title: 'Cabinet Maker',
    skills: ['Woodworking', 'CNC Operation', 'Finishing', 'Quality Control'],
    experience: '10 years',
    education: 'City & Guilds Furniture Production',
    bio: 'Skilled craftsman specializing in bespoke furniture',
    phone: '+44 7700 900116'
  },
  {
    id: 22,
    name: 'Jessica Taylor',
    email: 'jessica.taylor@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-12',
    location: 'Birmingham, UK',
    title: 'Upholsterer',
    skills: ['Upholstery', 'Pattern Making', 'Sewing', 'Fabric Selection'],
    experience: '5 years',
    education: 'NVQ Level 2 Furniture Production',
    bio: 'Creative upholsterer with attention to detail',
    phone: '+44 7700 900117'
  },
  {
    id: 23,
    name: 'Christopher Lee',
    email: 'chris.lee@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-05',
    location: 'Manchester, UK',
    title: 'Production Supervisor',
    skills: ['Team Leadership', 'Lean Manufacturing', 'Quality Assurance', 'Production Planning'],
    experience: '7 years',
    education: 'HND Manufacturing Engineering',
    bio: 'Production supervisor focused on efficiency and quality',
    phone: '+44 7700 900118'
  },
  // Candidates - Retail
  {
    id: 24,
    name: 'Hannah White',
    email: 'hannah.white@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-20',
    location: 'London, UK',
    title: 'Store Manager',
    skills: ['Team Management', 'Sales', 'Visual Merchandising', 'Stock Control'],
    experience: '6 years',
    education: 'BA Retail Management',
    bio: 'Results-driven retail manager with strong commercial awareness',
    phone: '+44 7700 900119'
  },
  {
    id: 25,
    name: 'Nathan Green',
    email: 'nathan.green@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-15',
    location: 'Manchester, UK',
    title: 'Sales Assistant',
    skills: ['Customer Service', 'Point of Sale', 'Product Knowledge', 'Cash Handling'],
    experience: '2 years',
    education: 'Level 2 Retail',
    bio: 'Enthusiastic sales professional with excellent customer service',
    phone: '+44 7700 900120'
  },
  // Candidates - Construction
  {
    id: 26,
    name: 'George Miller',
    email: 'george.miller@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-05',
    location: 'Milton Keynes, UK',
    title: 'Site Manager',
    skills: ['Project Management', 'Health & Safety', 'Building Regulations', 'Team Leadership'],
    experience: '12 years',
    education: 'HNC Construction',
    bio: 'Experienced site manager with strong H&S background',
    phone: '+44 7700 900121'
  },
  {
    id: 27,
    name: 'William Taylor',
    email: 'william.taylor@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-28',
    location: 'York, UK',
    title: 'Carpenter',
    skills: ['Carpentry', 'Joinery', 'Blueprint Reading', 'Power Tools'],
    experience: '8 years',
    education: 'NVQ Level 3 Carpentry',
    bio: 'Skilled carpenter with heritage building experience',
    phone: '+44 7700 900122'
  },
  // Candidates - Hospitality
  {
    id: 28,
    name: 'Charlotte King',
    email: 'charlotte.king@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-18',
    location: 'London, UK',
    title: 'Hotel Manager',
    skills: ['Operations Management', 'Guest Services', 'Budgeting', 'Staff Training'],
    experience: '9 years',
    education: 'BA Hospitality Management',
    bio: 'Hospitality professional dedicated to exceptional guest experiences',
    phone: '+44 7700 900123'
  },
  {
    id: 29,
    name: 'James Wright',
    email: 'james.wright@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-08',
    location: 'Lake District, UK',
    title: 'Chef de Partie',
    skills: ['Food Preparation', 'Menu Planning', 'Food Hygiene', 'Kitchen Management'],
    experience: '5 years',
    education: 'Professional Chef Diploma',
    bio: 'Passionate chef with fine dining experience',
    phone: '+44 7700 900124'
  },
  // More Candidates across various sectors
  {
    id: 30,
    name: 'Sophia Anderson',
    email: 'sophia.anderson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-20',
    location: 'Oxford, UK',
    title: 'Primary School Teacher',
    skills: ['Lesson Planning', 'Classroom Management', 'Assessment', 'Safeguarding'],
    experience: '6 years',
    education: 'PGCE Primary Education',
    bio: 'Dedicated teacher passionate about early years education',
    phone: '+44 7700 900125'
  },
  {
    id: 31,
    name: 'Benjamin Clarke',
    email: 'ben.clarke@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-15',
    location: 'Birmingham, UK',
    title: 'Training Coordinator',
    skills: ['Training Delivery', 'Curriculum Development', 'Assessment', 'E-Learning'],
    experience: '4 years',
    education: 'BA Education & Training',
    bio: 'Vocational trainer focused on skills development',
    phone: '+44 7700 900126'
  },
  {
    id: 32,
    name: 'Amelia Turner',
    email: 'amelia.turner@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-12',
    location: 'Derby, UK',
    title: 'Quality Control Inspector',
    skills: ['Quality Inspection', 'CMM Programming', 'ISO Standards', 'Documentation'],
    experience: '5 years',
    education: 'HNC Quality Management',
    bio: 'Detail-oriented QC professional with aerospace experience',
    phone: '+44 7700 900127'
  },
  {
    id: 33,
    name: 'Lucas Martin',
    email: 'lucas.martin@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-02',
    location: 'Nottingham, UK',
    title: 'Production Operative',
    skills: ['Machine Operation', 'Quality Checks', 'Health & Safety', 'Team Work'],
    experience: '3 years',
    education: 'Level 2 Manufacturing',
    bio: 'Reliable production worker with strong work ethic',
    phone: '+44 7700 900128'
  },
  {
    id: 34,
    name: 'Isabella Garcia',
    email: 'isabella.garcia@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-25',
    location: 'London, UK',
    title: 'Financial Analyst',
    skills: ['Financial Modeling', 'Excel', 'Risk Analysis', 'Reporting'],
    experience: '4 years',
    education: 'BSc Finance',
    bio: 'Finance professional with investment analysis expertise',
    phone: '+44 7700 900129'
  },
  {
    id: 35,
    name: 'Ethan Phillips',
    email: 'ethan.phillips@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-18',
    location: 'Leeds, UK',
    title: 'Accountant',
    skills: ['Bookkeeping', 'Tax Returns', 'Financial Statements', 'Sage'],
    experience: '5 years',
    education: 'ACCA Qualified',
    bio: 'Qualified accountant with SME experience',
    phone: '+44 7700 900130'
  },
  {
    id: 36,
    name: 'Mia Robinson',
    email: 'mia.robinson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-08',
    location: 'Liverpool, UK',
    title: 'Cleaning Supervisor',
    skills: ['Team Leadership', 'Quality Standards', 'Health & Safety', 'Client Relations'],
    experience: '6 years',
    education: 'COSHH Certificate',
    bio: 'Experienced cleaning supervisor with commercial expertise',
    phone: '+44 7700 900131'
  },
  {
    id: 37,
    name: 'Jack Thompson',
    email: 'jack.thompson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-22',
    location: 'London, UK',
    title: 'Digital Marketing Manager',
    skills: ['SEO', 'PPC', 'Social Media', 'Google Analytics', 'Content Strategy'],
    experience: '6 years',
    education: 'BA Marketing',
    bio: 'Digital marketing specialist with proven ROI results',
    phone: '+44 7700 900132'
  },
  {
    id: 38,
    name: 'Olivia Lewis',
    email: 'olivia.lewis@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-12',
    location: 'Manchester, UK',
    title: 'Solicitor',
    skills: ['Contract Law', 'Litigation', 'Legal Research', 'Client Advising'],
    experience: '4 years',
    education: 'LLB Law, LPC',
    bio: 'Qualified solicitor specializing in commercial law',
    phone: '+44 7700 900133'
  },
  {
    id: 39,
    name: 'Joshua Hill',
    email: 'joshua.hill@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-30',
    location: 'Cambridge, UK',
    title: 'Business Development Manager',
    skills: ['Sales Strategy', 'Networking', 'Presentations', 'CRM', 'Negotiation'],
    experience: '5 years',
    education: 'MBA',
    bio: 'Growth-focused business development professional',
    phone: '+44 7700 900134'
  },
  {
    id: 40,
    name: 'Emma Walker',
    email: 'emma.walker@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-25',
    location: 'Bristol, UK',
    title: 'Estate Agent',
    skills: ['Property Sales', 'Market Analysis', 'Negotiation', 'Client Relations'],
    experience: '3 years',
    education: 'NAEA Propertymark Qualified',
    bio: 'Energetic estate agent with local market knowledge',
    phone: '+44 7700 900135'
  },
  {
    id: 41,
    name: 'Noah Evans',
    email: 'noah.evans@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-15',
    location: 'Southampton, UK',
    title: 'Personal Trainer',
    skills: ['Fitness Coaching', 'Nutrition Planning', 'Client Assessment', 'Group Classes'],
    experience: '4 years',
    education: 'Level 3 Personal Training',
    bio: 'Certified trainer specializing in strength and conditioning',
    phone: '+44 7700 900136'
  },
  // Additional candidates to reach 80
  {
    id: 42,
    name: 'Grace Adams',
    email: 'grace.adams@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-05',
    location: 'Sheffield, UK',
    title: 'CNC Operator',
    skills: ['CNC Programming', 'Machine Setup', 'Quality Control', 'Blueprint Reading'],
    experience: '6 years',
    education: 'Level 3 Machining',
    bio: 'Skilled CNC operator with precision manufacturing experience',
    phone: '+44 7700 900137'
  },
  {
    id: 43,
    name: 'Daniel Wright',
    email: 'd.wright@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-18',
    location: 'Leeds, UK',
    title: 'Automation Engineer',
    skills: ['PLC Programming', 'SCADA', 'Robotics', 'System Integration'],
    experience: '5 years',
    education: 'MEng Automation',
    bio: 'Automation specialist focused on industrial systems',
    phone: '+44 7700 900138'
  },
  {
    id: 44,
    name: 'Chloe Mitchell',
    email: 'chloe.mitchell@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-30',
    location: 'Cambridge, UK',
    title: 'Renewable Energy Engineer',
    skills: ['Solar PV', 'Wind Energy', 'Energy Storage', 'Project Design'],
    experience: '4 years',
    education: 'MSc Renewable Energy',
    bio: 'Renewable energy engineer passionate about sustainability',
    phone: '+44 7700 900139'
  },
  {
    id: 45,
    name: 'Ryan Cooper',
    email: 'ryan.cooper@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-08',
    location: 'London, UK',
    title: 'Cloud Architect',
    skills: ['AWS', 'Azure', 'Infrastructure Design', 'Security', 'Cost Optimization'],
    experience: '7 years',
    education: 'BSc Computer Science',
    bio: 'Cloud solutions architect with enterprise experience',
    phone: '+44 7700 900140'
  },
  {
    id: 46,
    name: 'Emily Foster',
    email: 'emily.foster@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-12',
    location: 'Edinburgh, UK',
    title: 'Business Analyst',
    skills: ['Requirements Analysis', 'Process Mapping', 'SQL', 'Agile', 'Stakeholder Management'],
    experience: '5 years',
    education: 'BSc Business Analytics',
    bio: 'Business analyst bridging technology and business needs',
    phone: '+44 7700 900141'
  },
  {
    id: 47,
    name: 'Oscar Bailey',
    email: 'oscar.bailey@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-22',
    location: 'Manchester, UK',
    title: 'UX Designer',
    skills: ['User Research', 'Wireframing', 'Prototyping', 'Figma', 'User Testing'],
    experience: '4 years',
    education: 'BA Interaction Design',
    bio: 'UX designer focused on user-centered design',
    phone: '+44 7700 900142'
  },
  {
    id: 48,
    name: 'Lily Morgan',
    email: 'lily.morgan@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-18',
    location: 'Brighton, UK',
    title: 'Mobile Developer',
    skills: ['React Native', 'iOS', 'Android', 'Firebase', 'App Store Deployment'],
    experience: '3 years',
    education: 'BSc Software Engineering',
    bio: 'Mobile developer specializing in cross-platform apps',
    phone: '+44 7700 900143'
  },
  {
    id: 49,
    name: 'Harry Nelson',
    email: 'harry.nelson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-05',
    location: 'Reading, UK',
    title: 'IT Support Technician',
    skills: ['Technical Support', 'Windows Server', 'Active Directory', 'Troubleshooting'],
    experience: '3 years',
    education: 'CompTIA A+',
    bio: 'IT support professional with excellent problem-solving skills',
    phone: '+44 7700 900144'
  },
  {
    id: 50,
    name: 'Ava Richardson',
    email: 'ava.richardson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-22',
    location: 'Stevenage, UK',
    title: 'Care Team Leader',
    skills: ['Care Management', 'Staff Supervision', 'Care Planning', 'Medication Administration'],
    experience: '8 years',
    education: 'NVQ Level 5 Leadership',
    bio: 'Experienced care leader with strong team management skills',
    phone: '+44 7700 900145'
  },
  {
    id: 51,
    name: 'Charlie Wood',
    email: 'charlie.wood@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-28',
    location: 'Norwich, UK',
    title: 'Pharmacy Technician',
    skills: ['Dispensing', 'Stock Management', 'Patient Counseling', 'Prescription Processing'],
    experience: '4 years',
    education: 'GPhC Registered',
    bio: 'Qualified pharmacy technician with retail experience',
    phone: '+44 7700 900146'
  },
  {
    id: 52,
    name: 'Zoe Parker',
    email: 'zoe.parker@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-15',
    location: 'Birmingham, UK',
    title: 'HGV Driver',
    skills: ['Class 1 License', 'Tacho Compliance', 'Route Planning', 'Vehicle Checks'],
    experience: '10 years',
    education: 'HGV Class 1 License',
    bio: 'Experienced HGV driver with clean license',
    phone: '+44 7700 900147'
  },
  {
    id: 53,
    name: 'Leo Carter',
    email: 'leo.carter@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-28',
    location: 'Bristol, UK',
    title: 'Courier Driver',
    skills: ['Driving', 'Time Management', 'Customer Service', 'Navigation'],
    experience: '2 years',
    education: 'Full UK License',
    bio: 'Reliable courier with excellent timekeeping',
    phone: '+44 7700 900148'
  },
  {
    id: 54,
    name: 'Freya Bennett',
    email: 'freya.bennett@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-10',
    location: 'Milton Keynes, UK',
    title: 'Body Shop Technician',
    skills: ['Panel Beating', 'Spray Painting', 'Welding', 'Insurance Claims'],
    experience: '6 years',
    education: 'IMI Level 3',
    bio: 'Skilled body shop technician with insurance work experience',
    phone: '+44 7700 900149'
  },
  {
    id: 55,
    name: 'Mason Hughes',
    email: 'mason.hughes@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-20',
    location: 'Oxford, UK',
    title: 'MOT Tester',
    skills: ['MOT Testing', 'Vehicle Inspection', 'Diagnostics', 'Customer Service'],
    experience: '5 years',
    education: 'MOT Tester Qualification',
    bio: 'Qualified MOT tester with high pass rate',
    phone: '+44 7700 900150'
  },
  {
    id: 56,
    name: 'Poppy Russell',
    email: 'poppy.russell@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-12',
    location: 'High Wycombe, UK',
    title: 'French Polisher',
    skills: ['Wood Finishing', 'Restoration', 'Spray Finishing', 'Color Matching'],
    experience: '12 years',
    education: 'Traditional Apprenticeship',
    bio: 'Master craftsman specializing in traditional finishes',
    phone: '+44 7700 900151'
  },
  {
    id: 57,
    name: 'Max Griffin',
    email: 'max.griffin@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-18',
    location: 'Birmingham, UK',
    title: 'Furniture Designer',
    skills: ['CAD Design', '3D Modeling', 'Prototyping', 'Material Selection'],
    experience: '5 years',
    education: 'BA Furniture Design',
    bio: 'Creative designer blending form and function',
    phone: '+44 7700 900152'
  },
  {
    id: 58,
    name: 'Daisy Russell',
    email: 'daisy.russell@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-22',
    location: 'Manchester, UK',
    title: 'Assembly Operator',
    skills: ['Product Assembly', 'Hand Tools', 'Quality Inspection', 'Team Work'],
    experience: '3 years',
    education: 'Level 2 Manufacturing',
    bio: 'Detail-oriented assembly worker with quality focus',
    phone: '+44 7700 900153'
  },
  {
    id: 59,
    name: 'Alfie Stewart',
    email: 'alfie.stewart@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-05',
    location: 'London, UK',
    title: 'Visual Merchandiser',
    skills: ['Display Design', 'Window Dressing', 'Trend Analysis', 'Creative Direction'],
    experience: '4 years',
    education: 'BA Visual Merchandising',
    bio: 'Creative merchandiser with retail design expertise',
    phone: '+44 7700 900154'
  },
  {
    id: 60,
    name: 'Ruby Cook',
    email: 'ruby.cook@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-28',
    location: 'Bath, UK',
    title: 'Retail Supervisor',
    skills: ['Team Leadership', 'Stock Management', 'Customer Service', 'Till Operations'],
    experience: '5 years',
    education: 'Level 3 Retail Management',
    bio: 'Experienced retail supervisor with fashion background',
    phone: '+44 7700 900155'
  },
  {
    id: 61,
    name: 'Theo Morris',
    email: 'theo.morris@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-08',
    location: 'Milton Keynes, UK',
    title: 'Bricklayer',
    skills: ['Bricklaying', 'Blockwork', 'Pointing', 'Blueprint Reading'],
    experience: '9 years',
    education: 'NVQ Level 3 Bricklaying',
    bio: 'Skilled bricklayer with commercial and residential experience',
    phone: '+44 7700 900156'
  },
  {
    id: 62,
    name: 'Ella Powell',
    email: 'ella.powell@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-28',
    location: 'York, UK',
    title: 'Stone Mason',
    skills: ['Stone Carving', 'Restoration', 'Heritage Work', 'Traditional Techniques'],
    experience: '7 years',
    education: 'City & Guilds Stonemasonry',
    bio: 'Traditional stone mason with heritage building expertise',
    phone: '+44 7700 900157'
  },
  {
    id: 63,
    name: 'Arthur Long',
    email: 'arthur.long@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-12',
    location: 'London, UK',
    title: 'Concierge',
    skills: ['Guest Relations', 'Problem Solving', 'Local Knowledge', 'Multi-lingual'],
    experience: '6 years',
    education: 'Hospitality Diploma',
    bio: 'Professional concierge with luxury hotel experience',
    phone: '+44 7700 900158'
  },
  {
    id: 64,
    name: 'Rosie Edwards',
    email: 'rosie.edwards@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-18',
    location: 'Lake District, UK',
    title: 'Housekeeper',
    skills: ['Cleaning Standards', 'Attention to Detail', 'Time Management', 'Laundry'],
    experience: '4 years',
    education: 'Housekeeping Certificate',
    bio: 'Professional housekeeper with high standards',
    phone: '+44 7700 900159'
  },
  {
    id: 65,
    name: 'Jacob Barnes',
    email: 'jacob.barnes@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-25',
    location: 'Oxford, UK',
    title: 'Teaching Assistant',
    skills: ['Classroom Support', 'SEN Support', 'Behavior Management', 'Lesson Preparation'],
    experience: '3 years',
    education: 'Level 3 Teaching Assistant',
    bio: 'Dedicated teaching assistant supporting primary education',
    phone: '+44 7700 900160'
  },
  {
    id: 66,
    name: 'Matilda Ward',
    email: 'matilda.ward@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-15',
    location: 'Birmingham, UK',
    title: 'Apprenticeship Assessor',
    skills: ['Assessment', 'Mentoring', 'Quality Assurance', 'Industry Knowledge'],
    experience: '5 years',
    education: 'TAQA Qualification',
    bio: 'Assessor supporting apprentice development',
    phone: '+44 7700 900161'
  },
  {
    id: 67,
    name: 'Henry Foster',
    email: 'henry.foster@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-15',
    location: 'Derby, UK',
    title: 'Mechanical Fitter',
    skills: ['Assembly', 'Hydraulics', 'Pneumatics', 'Maintenance'],
    experience: '7 years',
    education: 'Level 3 Mechanical Fitting',
    bio: 'Skilled fitter with aerospace background',
    phone: '+44 7700 900162'
  },
  {
    id: 68,
    name: 'Evie Palmer',
    email: 'evie.palmer@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-08',
    location: 'Nottingham, UK',
    title: 'Packaging Operative',
    skills: ['Packaging', 'Quality Control', 'Machine Operation', 'Health & Safety'],
    experience: '2 years',
    education: 'Level 1 Manufacturing',
    bio: 'Reliable packaging operative with quality focus',
    phone: '+44 7700 900163'
  },
  {
    id: 69,
    name: 'George Fisher',
    email: 'george.fisher@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-22',
    location: 'London, UK',
    title: 'Investment Analyst',
    skills: ['Financial Analysis', 'Market Research', 'Portfolio Management', 'Bloomberg'],
    experience: '4 years',
    education: 'MSc Finance',
    bio: 'Analyst specializing in equity research',
    phone: '+44 7700 900164'
  },
  {
    id: 70,
    name: 'Isabelle Gray',
    email: 'isabelle.gray@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-28',
    location: 'Leeds, UK',
    title: 'Bookkeeper',
    skills: ['Bookkeeping', 'Payroll', 'VAT Returns', 'Xero', 'QuickBooks'],
    experience: '6 years',
    education: 'AAT Level 3',
    bio: 'Experienced bookkeeper with small business focus',
    phone: '+44 7700 900165'
  },
  {
    id: 71,
    name: 'Tommy Hughes',
    email: 'tommy.hughes@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-12',
    location: 'Liverpool, UK',
    title: 'Cleaning Operative',
    skills: ['Commercial Cleaning', 'Floor Care', 'Window Cleaning', 'COSHH'],
    experience: '4 years',
    education: 'Health & Safety Certificate',
    bio: 'Reliable cleaning operative with commercial experience',
    phone: '+44 7700 900166'
  },
  {
    id: 72,
    name: 'Scarlett Watson',
    email: 'scarlett.watson@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-08',
    location: 'London, UK',
    title: 'Social Media Manager',
    skills: ['Social Media Strategy', 'Content Creation', 'Analytics', 'Influencer Marketing'],
    experience: '5 years',
    education: 'BA Digital Media',
    bio: 'Creative social media manager with brand building experience',
    phone: '+44 7700 900167'
  },
  {
    id: 73,
    name: 'Finley Scott',
    email: 'finley.scott@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-22',
    location: 'Manchester, UK',
    title: 'Paralegal',
    skills: ['Legal Research', 'Document Preparation', 'Case Management', 'Client Communication'],
    experience: '3 years',
    education: 'Graduate Diploma in Law',
    bio: 'Paralegal supporting commercial law practice',
    phone: '+44 7700 900168'
  },
  {
    id: 74,
    name: 'Willow Price',
    email: 'willow.price@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-18',
    location: 'Cambridge, UK',
    title: 'Startup Consultant',
    skills: ['Business Planning', 'Pitch Development', 'Funding Strategy', 'Mentoring'],
    experience: '6 years',
    education: 'MBA Entrepreneurship',
    bio: 'Consultant helping startups scale and secure funding',
    phone: '+44 7700 900169'
  },
  {
    id: 75,
    name: 'Harrison Bell',
    email: 'harrison.bell@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-03-05',
    location: 'Bristol, UK',
    title: 'Lettings Negotiator',
    skills: ['Property Lettings', 'Tenant Screening', 'Contract Negotiation', 'Property Viewings'],
    experience: '3 years',
    education: 'ARLA Propertymark',
    bio: 'Lettings professional with strong negotiation skills',
    phone: '+44 7700 900170'
  },
  {
    id: 76,
    name: 'Phoebe Cooper',
    email: 'phoebe.cooper@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-04-15',
    location: 'Southampton, UK',
    title: 'Fitness Instructor',
    skills: ['Group Classes', 'HIIT', 'Spin', 'First Aid', 'Customer Engagement'],
    experience: '3 years',
    education: 'Level 2 Gym Instructor',
    bio: 'Energetic instructor specializing in group fitness',
    phone: '+44 7700 900171'
  },
  {
    id: 77,
    name: 'Samuel Reed',
    email: 'samuel.reed@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-05-28',
    location: 'Sheffield, UK',
    title: 'Welder',
    skills: ['MIG Welding', 'TIG Welding', 'Blueprint Reading', 'Fabrication'],
    experience: '8 years',
    education: 'Level 3 Welding',
    bio: 'Certified welder with structural steel experience',
    phone: '+44 7700 900172'
  },
  {
    id: 78,
    name: 'Millie Turner',
    email: 'millie.turner@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-02-20',
    location: 'Cambridge, UK',
    title: 'Research Scientist',
    skills: ['Laboratory Research', 'Data Analysis', 'Technical Writing', 'Project Management'],
    experience: '4 years',
    education: 'PhD Chemistry',
    bio: 'Research scientist focused on renewable materials',
    phone: '+44 7700 900173'
  },
  {
    id: 79,
    name: 'Jude Morgan',
    email: 'jude.morgan@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-06-10',
    location: 'London, UK',
    title: 'Solutions Architect',
    skills: ['System Design', 'Microservices', 'API Design', 'Technical Leadership'],
    experience: '9 years',
    education: 'MSc Software Engineering',
    bio: 'Senior architect designing enterprise solutions',
    phone: '+44 7700 900174'
  },
  {
    id: 80,
    name: 'Eliza Bennett',
    email: 'eliza.bennett@email.com',
    type: 'Candidate',
    status: 'active',
    joinDate: '2024-01-15',
    location: 'Manchester, UK',
    title: 'Product Manager',
    skills: ['Product Strategy', 'Roadmap Planning', 'Agile', 'User Research', 'Analytics'],
    experience: '6 years',
    education: 'BSc Product Design',
    bio: 'Product manager with B2B SaaS experience',
    phone: '+44 7700 900175'
  },
];

// JOBS DATA (35 jobs across various companies and sectors)
export const jobs: Job[] = [
  // Furniture Manufacturing Jobs
  {
    id: 1,
    title: 'Cabinet Maker',
    companyId: 1,
    location: 'High Wycombe, UK',
    salary: '£26,000 - £32,000',
    type: 'Full-time',
    posted: '2 days ago',
    status: 'active',
    description: 'Skilled cabinet maker needed for bespoke furniture production. Join our traditional workshop creating high-quality oak furniture.',
    requirements: ['5+ years cabinet making experience', 'Traditional joinery skills', 'CNC operation', 'Quality control'],
    benefits: ['Pension scheme', 'Training provided', 'Employee discount', 'Workshop tools provided'],
    applicants: 12,
    views: 145,
    reports: 0,
    verified: true,
    category: 'Manufacturing'
  },
  {
    id: 2,
    title: 'Furniture Designer',
    companyId: 2,
    location: 'Birmingham, UK',
    salary: '£30,000 - £38,000',
    type: 'Full-time',
    posted: '5 days ago',
    status: 'active',
    description: 'Creative furniture designer to develop contemporary furniture ranges using CAD and 3D modeling.',
    requirements: ['CAD proficiency', 'Portfolio of work', 'Material knowledge', 'Prototyping experience'],
    benefits: ['Flexible hours', 'Creative freedom', 'Professional development', 'Design software provided'],
    applicants: 8,
    views: 98,
    reports: 0,
    verified: true,
    urgent: true,
    category: 'Design'
  },
  {
    id: 3,
    title: 'Upholstery Specialist',
    companyId: 3,
    location: 'Manchester, UK',
    salary: '£24,000 - £28,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Experienced upholsterer for ergonomic office furniture production line.',
    requirements: ['Upholstery experience', 'Pattern making', 'Industrial sewing', 'Quality standards'],
    benefits: ['Pension', 'Training', 'Career progression', 'Employee benefits'],
    applicants: 15,
    views: 167,
    reports: 0,
    verified: true,
    category: 'Manufacturing'
  },
  // Engineering Jobs
  {
    id: 4,
    title: 'Senior Mechanical Engineer',
    companyId: 4,
    location: 'Sheffield, UK',
    salary: '£45,000 - £55,000',
    type: 'Full-time',
    posted: '3 days ago',
    status: 'active',
    description: 'Lead mechanical engineer for precision manufacturing projects. Work on cutting-edge aerospace components.',
    requirements: ['8+ years experience', 'CAD expertise', 'Project management', 'Quality standards'],
    benefits: ['Competitive salary', 'Bonus scheme', 'Private healthcare', 'Pension'],
    applicants: 24,
    views: 289,
    reports: 0,
    verified: true,
    urgent: true,
    category: 'Engineering'
  },
  {
    id: 5,
    title: 'Electrical Engineer',
    companyId: 5,
    location: 'Leeds, UK',
    salary: '£35,000 - £42,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Industrial automation engineer to design and implement control systems.',
    requirements: ['PLC programming', 'AutoCAD', 'Electrical design', 'Safety standards'],
    benefits: ['Pension', 'Training budget', 'Company vehicle', 'Overtime available'],
    applicants: 18,
    views: 203,
    reports: 0,
    verified: true,
    category: 'Engineering'
  },
  {
    id: 6,
    title: 'Renewable Energy Engineer',
    companyId: 7,
    location: 'Cambridge, UK',
    salary: '£38,000 - £46,000',
    type: 'Full-time',
    posted: '4 days ago',
    status: 'active',
    description: 'Join our team developing innovative renewable energy solutions including solar and wind projects.',
    requirements: ['Renewable energy experience', 'Project design', 'System integration', 'Technical reporting'],
    benefits: ['Green company car', 'Flexible working', 'Professional development', 'Bonus scheme'],
    applicants: 20,
    views: 234,
    reports: 0,
    verified: true,
    category: 'Engineering'
  },
  // Automotive Jobs
  {
    id: 7,
    title: 'Automotive Technician',
    companyId: 8,
    location: 'Milton Keynes, UK',
    salary: '£26,000 - £32,000',
    type: 'Full-time',
    posted: '2 days ago',
    status: 'active',
    description: 'Qualified technician for busy service center. Diagnostics, repairs, and MOT testing.',
    requirements: ['Level 3 qualified', 'MOT license', 'Diagnostic experience', 'Customer service'],
    benefits: ['Tool allowance', 'Bonus scheme', 'Training', 'Uniform provided'],
    applicants: 16,
    views: 178,
    reports: 0,
    verified: true,
    category: 'Automotive'
  },
  {
    id: 8,
    title: 'Fleet Mechanic',
    companyId: 9,
    location: 'Coventry, UK',
    salary: '£30,000 - £36,000',
    type: 'Full-time',
    posted: '5 days ago',
    status: 'active',
    description: 'Commercial vehicle mechanic for national fleet operator. HGV experience essential.',
    requirements: ['HGV maintenance', 'Preventive maintenance', 'Diagnostics', 'Record keeping'],
    benefits: ['Overtime', 'Pension', 'Training', 'Company benefits'],
    applicants: 11,
    views: 142,
    reports: 0,
    verified: true,
    category: 'Automotive'
  },
  // IT & Technology Jobs
  {
    id: 9,
    title: 'Full Stack Developer',
    companyId: 11,
    location: 'London, UK',
    salary: '£45,000 - £60,000',
    type: 'Full-time',
    posted: '1 day ago',
    status: 'active',
    description: 'Full stack developer to build cloud-native applications. React, Node.js, AWS experience required.',
    requirements: ['React expertise', 'Node.js', 'AWS services', 'Agile experience'],
    benefits: ['Remote working', 'Stock options', 'Learning budget', '25 days holiday'],
    applicants: 45,
    views: 512,
    reports: 0,
    verified: true,
    urgent: true,
    remote: true,
    category: 'Technology'
  },
  {
    id: 10,
    title: 'DevOps Engineer',
    companyId: 11,
    location: 'London, UK',
    salary: '£50,000 - £65,000',
    type: 'Full-time',
    posted: '3 days ago',
    status: 'active',
    description: 'DevOps engineer to manage cloud infrastructure and CI/CD pipelines.',
    requirements: ['Kubernetes', 'Docker', 'Terraform', 'AWS/Azure', 'CI/CD'],
    benefits: ['Remote options', 'Bonus', 'Conferences', 'Equipment budget'],
    applicants: 38,
    views: 445,
    reports: 0,
    verified: true,
    remote: true,
    category: 'Technology'
  },
  {
    id: 11,
    title: 'Data Analyst',
    companyId: 12,
    location: 'Edinburgh, UK',
    salary: '£35,000 - £45,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Data analyst to extract insights from big data and create business intelligence dashboards.',
    requirements: ['Python/SQL', 'Power BI', 'Statistical analysis', 'Stakeholder management'],
    benefits: ['Hybrid working', 'Training budget', 'Pension', 'Health insurance'],
    applicants: 27,
    views: 298,
    reports: 0,
    verified: true,
    category: 'Technology'
  },
  {
    id: 12,
    title: 'Cybersecurity Analyst',
    companyId: 14,
    location: 'Reading, UK',
    salary: '£40,000 - £50,000',
    type: 'Full-time',
    posted: '4 days ago',
    status: 'active',
    description: 'Security analyst to monitor threats and implement security controls.',
    requirements: ['Security certifications', 'SIEM experience', 'Incident response', 'Risk assessment'],
    benefits: ['Certification support', 'Bonus', 'Flexible hours', 'Career development'],
    applicants: 22,
    views: 256,
    reports: 0,
    verified: true,
    category: 'Technology'
  },
  // Logistics Jobs
  {
    id: 13,
    title: 'Delivery Driver',
    companyId: 16,
    location: 'Birmingham, UK',
    salary: '£24,000 - £28,000',
    type: 'Full-time',
    posted: '1 day ago',
    status: 'active',
    description: 'Multi-drop delivery driver for national courier service. Van provided.',
    requirements: ['Full UK license', 'Clean driving record', 'Customer service', 'Physical fitness'],
    benefits: ['Company van', 'Fuel card', 'Overtime', 'Pension'],
    applicants: 42,
    views: 387,
    reports: 0,
    verified: true,
    urgent: true,
    category: 'Logistics'
  },
  {
    id: 14,
    title: 'Warehouse Supervisor',
    companyId: 17,
    location: 'Leeds, UK',
    salary: '£28,000 - £34,000',
    type: 'Full-time',
    posted: '5 days ago',
    status: 'active',
    description: 'Warehouse supervisor to manage team and ensure smooth operations.',
    requirements: ['Warehouse management', 'Team leadership', 'Health & Safety', 'Forklift license'],
    benefits: ['Bonus', 'Training', 'Pension', 'Career progression'],
    applicants: 19,
    views: 212,
    reports: 0,
    verified: true,
    category: 'Logistics'
  },
  {
    id: 15,
    title: 'HGV Driver - Class 1',
    companyId: 16,
    location: 'Various, UK',
    salary: '£32,000 - £40,000',
    type: 'Full-time',
    posted: '2 days ago',
    status: 'active',
    description: 'Class 1 HGV driver for long-distance deliveries. Excellent rates and overnight allowances.',
    requirements: ['HGV Class 1', 'CPC card', 'Digital tacho', 'Clean license'],
    benefits: ['High earnings', 'Overnight allowance', 'Modern fleet', 'Pension'],
    applicants: 28,
    views: 324,
    reports: 0,
    verified: true,
    category: 'Logistics'
  },
  // Healthcare Jobs
  {
    id: 16,
    title: 'Senior Care Assistant',
    companyId: 19,
    location: 'Stevenage, UK',
    salary: '£28,000 - £32,000',
    type: 'Full-time',
    posted: '2 days ago',
    status: 'active',
    description: 'Experienced care assistant to provide high-quality patient care in hospital setting.',
    requirements: ['NVQ Level 3', '5+ years experience', 'Patient care', 'Medication administration'],
    benefits: ['NHS pension', 'Training', 'Career development', '27 days holiday'],
    applicants: 31,
    views: 356,
    reports: 0,
    matchScore: 94,
    verified: true,
    category: 'Healthcare'
  },
  {
    id: 17,
    title: 'Healthcare Support Worker',
    companyId: 20,
    location: 'Cambridge, UK',
    salary: '£24,000 - £27,000',
    type: 'Part-time',
    posted: '5 days ago',
    status: 'active',
    description: 'Support workers needed for home care visits. Flexible shifts available.',
    requirements: ['Care certificate', 'Driving license', 'Compassionate nature', 'Good communication'],
    benefits: ['Mileage paid', 'Flexible hours', 'Training', 'Career progression'],
    applicants: 25,
    views: 287,
    reports: 0,
    matchScore: 87,
    verified: true,
    category: 'Healthcare'
  },
  {
    id: 18,
    title: 'Registered Nurse',
    companyId: 21,
    location: 'Norwich, UK',
    salary: '£32,000 - £38,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'RGN for private medical center. General practice and minor procedures.',
    requirements: ['NMC registration', 'Clinical experience', 'Patient assessment', 'Team work'],
    benefits: ['Private healthcare', 'Pension', 'CPD support', 'Parking'],
    applicants: 18,
    views: 201,
    reports: 0,
    verified: true,
    category: 'Healthcare'
  },
  // Retail Jobs
  {
    id: 19,
    title: 'Store Manager',
    companyId: 22,
    location: 'London, UK',
    salary: '£32,000 - £40,000',
    type: 'Full-time',
    posted: '3 days ago',
    status: 'active',
    description: 'Store manager for busy supermarket. Lead a team and drive sales performance.',
    requirements: ['Retail management', 'P&L responsibility', 'Team leadership', 'Commercial awareness'],
    benefits: ['Bonus scheme', 'Staff discount', 'Pension', 'Career progression'],
    applicants: 22,
    views: 245,
    reports: 0,
    verified: true,
    category: 'Retail'
  },
  {
    id: 20,
    title: 'Sales Assistant',
    companyId: 23,
    location: 'Manchester, UK',
    salary: '£20,000 - £24,000',
    type: 'Full-time',
    posted: '4 days ago',
    status: 'active',
    description: 'Sales assistant for electronics retail store. Product knowledge and customer service focus.',
    requirements: ['Customer service', 'Sales experience', 'Tech knowledge', 'Team player'],
    benefits: ['Staff discount', 'Bonus', 'Training', 'Pension'],
    applicants: 34,
    views: 289,
    reports: 0,
    verified: true,
    category: 'Retail'
  },
  // Construction Jobs
  {
    id: 21,
    title: 'Site Manager',
    companyId: 25,
    location: 'Milton Keynes, UK',
    salary: '£45,000 - £55,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Site manager for commercial construction projects. SMSTS required.',
    requirements: ['SMSTS', '10+ years experience', 'Project management', 'Health & Safety'],
    benefits: ['Company car', 'Bonus', 'Pension', 'Professional development'],
    applicants: 16,
    views: 187,
    reports: 0,
    verified: true,
    category: 'Construction'
  },
  {
    id: 22,
    title: 'Carpenter',
    companyId: 26,
    location: 'York, UK',
    salary: '£30,000 - £36,000',
    type: 'Full-time',
    posted: '5 days ago',
    status: 'active',
    description: 'Skilled carpenter for heritage building restoration projects.',
    requirements: ['Carpentry qualification', 'Traditional skills', 'Heritage experience', 'Attention to detail'],
    benefits: ['Specialist work', 'Training', 'Pension', 'Tool allowance'],
    applicants: 12,
    views: 145,
    reports: 0,
    verified: true,
    category: 'Construction'
  },
  // Hospitality Jobs
  {
    id: 23,
    title: 'Hotel Manager',
    companyId: 27,
    location: 'London, UK',
    salary: '£40,000 - £50,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Hotel manager for luxury London property. Lead operations and guest services.',
    requirements: ['Hotel management', 'Team leadership', 'Budgeting', 'Guest relations'],
    benefits: ['Performance bonus', 'Healthcare', 'Accommodation discount', 'Pension'],
    applicants: 19,
    views: 221,
    reports: 0,
    verified: true,
    category: 'Hospitality'
  },
  {
    id: 24,
    title: 'Chef de Partie',
    companyId: 28,
    location: 'Lake District, UK',
    salary: '£26,000 - £30,000',
    type: 'Full-time',
    posted: '4 days ago',
    status: 'active',
    description: 'CDP for award-winning country hotel restaurant. Fine dining experience.',
    requirements: ['Professional chef qualification', 'Section experience', 'Food hygiene', 'Passion for food'],
    benefits: ['Accommodation', 'Meals on duty', 'Tips', 'Beautiful location'],
    applicants: 21,
    views: 243,
    reports: 0,
    verified: true,
    category: 'Hospitality'
  },
  // Education Jobs
  {
    id: 25,
    title: 'Primary School Teacher',
    companyId: 29,
    location: 'Oxford, UK',
    salary: '£30,000 - £42,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Qualified teacher for Year 3 class. Join our outstanding independent school.',
    requirements: ['QTS', 'Classroom management', 'Curriculum knowledge', 'Pastoral care'],
    benefits: ['Pension', 'Professional development', 'Small class sizes', 'School holidays'],
    applicants: 15,
    views: 176,
    reports: 0,
    verified: true,
    category: 'Education'
  },
  {
    id: 26,
    title: 'Training Coordinator',
    companyId: 30,
    location: 'Birmingham, UK',
    salary: '£26,000 - £32,000',
    type: 'Full-time',
    posted: '5 days ago',
    status: 'active',
    description: 'Vocational training coordinator to deliver apprenticeship programs.',
    requirements: ['Teaching qualification', 'Industry experience', 'Assessment', 'Communication'],
    benefits: ['Term time only', 'Training', 'Pension', 'Flexible working'],
    applicants: 11,
    views: 134,
    reports: 0,
    verified: true,
    category: 'Education'
  },
  // Manufacturing Jobs
  {
    id: 27,
    title: 'CNC Operator',
    companyId: 31,
    location: 'Derby, UK',
    salary: '£28,000 - £34,000',
    type: 'Full-time',
    posted: '3 days ago',
    status: 'active',
    description: 'CNC operator for aerospace component manufacturing. 5-axis experience preferred.',
    requirements: ['CNC programming', 'Blueprint reading', 'Quality control', 'Tool setting'],
    benefits: ['Shift allowance', 'Overtime', 'Pension', 'Training'],
    applicants: 18,
    views: 198,
    reports: 0,
    verified: true,
    category: 'Manufacturing'
  },
  {
    id: 28,
    title: 'Production Operative',
    companyId: 32,
    location: 'Nottingham, UK',
    salary: '£22,000 - £26,000',
    type: 'Full-time',
    posted: '2 days ago',
    status: 'active',
    description: 'Production operative for packaging manufacturing. Full training provided.',
    requirements: ['Attention to detail', 'Team work', 'Health & Safety', 'Flexibility'],
    benefits: ['Overtime', 'Pension', 'Free parking', 'Canteen'],
    applicants: 26,
    views: 278,
    reports: 0,
    verified: true,
    category: 'Manufacturing'
  },
  // Finance Jobs
  {
    id: 29,
    title: 'Financial Analyst',
    companyId: 33,
    location: 'London, UK',
    salary: '£40,000 - £50,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Financial analyst for investment firm. Equity research and portfolio analysis.',
    requirements: ['Finance degree', 'Financial modeling', 'Excel', 'CFA progress'],
    benefits: ['Bonus', 'Healthcare', 'Pension', 'Study support'],
    applicants: 29,
    views: 312,
    reports: 0,
    verified: true,
    category: 'Finance'
  },
  {
    id: 30,
    title: 'Accountant',
    companyId: 34,
    location: 'Leeds, UK',
    salary: '£30,000 - £38,000',
    type: 'Full-time',
    posted: '4 days ago',
    status: 'active',
    description: 'Practice accountant to manage client portfolio. ACCA/ACA qualified.',
    requirements: ['Accounting qualification', 'Client management', 'Tax knowledge', 'Software skills'],
    benefits: ['Flexible hours', 'Study leave', 'Bonus', 'Pension'],
    applicants: 17,
    views: 189,
    reports: 0,
    verified: true,
    category: 'Finance'
  },
  // Additional Jobs
  {
    id: 31,
    title: 'Cleaning Supervisor',
    companyId: 35,
    location: 'Liverpool, UK',
    salary: '£24,000 - £28,000',
    type: 'Full-time',
    posted: '3 days ago',
    status: 'active',
    description: 'Cleaning supervisor for commercial contracts. Team leadership and quality control.',
    requirements: ['Supervisory experience', 'COSHH', 'Client relations', 'Quality standards'],
    benefits: ['Company vehicle', 'Uniform', 'Pension', 'Training'],
    applicants: 14,
    views: 156,
    reports: 0,
    verified: true,
    category: 'Facilities'
  },
  {
    id: 32,
    title: 'Digital Marketing Manager',
    companyId: 36,
    location: 'London, UK',
    salary: '£38,000 - £48,000',
    type: 'Full-time',
    posted: '5 days ago',
    status: 'active',
    description: 'Marketing manager to lead digital campaigns for creative agency clients.',
    requirements: ['Digital marketing', 'SEO/PPC', 'Analytics', 'Client management'],
    benefits: ['Hybrid working', 'Creative environment', 'Bonus', 'Tech allowance'],
    applicants: 32,
    views: 367,
    reports: 0,
    verified: true,
    category: 'Marketing'
  },
  {
    id: 33,
    title: 'Solicitor',
    companyId: 37,
    location: 'Manchester, UK',
    salary: '£45,000 - £60,000',
    type: 'Full-time',
    posted: '1 week ago',
    status: 'active',
    description: 'Commercial solicitor for growing practice. 3+ years PQE required.',
    requirements: ['Qualified solicitor', '3+ PQE', 'Commercial law', 'Client care'],
    benefits: ['Competitive salary', 'Bonus', 'Healthcare', 'CPD support'],
    applicants: 13,
    views: 148,
    reports: 0,
    verified: true,
    category: 'Legal'
  },
  {
    id: 34,
    title: 'Estate Agent',
    companyId: 39,
    location: 'Bristol, UK',
    salary: '£22,000 + Commission',
    type: 'Full-time',
    posted: '2 days ago',
    status: 'active',
    description: 'Sales negotiator for busy estate agency. Excellent earning potential.',
    requirements: ['Sales experience', 'Driving license', 'Communication', 'Local knowledge'],
    benefits: ['High commission', 'Company car', 'Bonus', 'Training'],
    applicants: 23,
    views: 256,
    reports: 0,
    verified: true,
    category: 'Real Estate'
  },
  {
    id: 35,
    title: 'Personal Trainer',
    companyId: 40,
    location: 'Southampton, UK',
    salary: '£24,000 - £35,000',
    type: 'Full-time',
    posted: '4 days ago',
    status: 'active',
    description: 'Personal trainer for premium gym. Build your client base with our support.',
    requirements: ['Level 3 PT', 'First aid', 'Client engagement', 'Nutrition knowledge'],
    benefits: ['Gym membership', 'CPD budget', 'Flexible hours', 'Commission'],
    applicants: 19,
    views: 212,
    reports: 0,
    verified: true,
    category: 'Health & Fitness'
  },
];

// CONVERSATIONS DATA (25 conversations)
export const conversations: Conversation[] = [
  {
    id: 1,
    candidateId: 13,
    employerId: 5,
    jobId: 16,
    lastMessage: 'Thank you! I look forward to the interview on Friday.',
    lastMessageTime: '2025-11-01 14:30',
    unread: 0,
    status: 'active'
  },
  {
    id: 2,
    candidateId: 9,
    employerId: 3,
    jobId: 9,
    lastMessage: 'Could you tell me more about the tech stack?',
    lastMessageTime: '2025-11-01 16:45',
    unread: 1,
    status: 'active'
  },
  {
    id: 3,
    candidateId: 6,
    employerId: 2,
    jobId: 4,
    lastMessage: 'I have 8 years of CAD experience with SolidWorks.',
    lastMessageTime: '2025-10-31 11:20',
    unread: 0,
    status: 'active'
  },
  {
    id: 4,
    candidateId: 16,
    employerId: 4,
    jobId: 13,
    lastMessage: 'Yes, I can start immediately.',
    lastMessageTime: '2025-11-01 09:15',
    unread: 2,
    status: 'active'
  },
  {
    id: 5,
    candidateId: 10,
    employerId: 3,
    jobId: 10,
    lastMessage: 'I am interested in your DevOps position.',
    lastMessageTime: '2025-10-31 15:30',
    unread: 0,
    status: 'active'
  },
  {
    id: 6,
    candidateId: 19,
    employerId: 1,
    jobId: 7,
    lastMessage: 'I have my Level 3 and MOT license.',
    lastMessageTime: '2025-10-30 14:00',
    unread: 1,
    status: 'active'
  },
  {
    id: 7,
    candidateId: 21,
    employerId: 1,
    jobId: 1,
    lastMessage: 'My portfolio shows 10 years of bespoke work.',
    lastMessageTime: '2025-10-31 10:45',
    unread: 0,
    status: 'active'
  },
  {
    id: 8,
    candidateId: 14,
    employerId: 5,
    jobId: 17,
    lastMessage: 'I am available for flexible shifts.',
    lastMessageTime: '2025-11-01 08:20',
    unread: 1,
    status: 'active'
  },
  {
    id: 9,
    candidateId: 7,
    employerId: 2,
    jobId: 5,
    lastMessage: 'I have extensive PLC programming experience.',
    lastMessageTime: '2025-10-30 16:30',
    unread: 0,
    status: 'active'
  },
  {
    id: 10,
    candidateId: 24,
    employerId: 1,
    jobId: 19,
    lastMessage: 'I managed a team of 15 at my previous role.',
    lastMessageTime: '2025-10-29 13:45',
    unread: 0,
    status: 'active'
  },
  {
    id: 11,
    candidateId: 11,
    employerId: 3,
    jobId: 11,
    lastMessage: 'My Python and SQL skills are advanced level.',
    lastMessageTime: '2025-10-31 12:00',
    unread: 1,
    status: 'active'
  },
  {
    id: 12,
    candidateId: 17,
    employerId: 4,
    jobId: 14,
    lastMessage: 'I have my forklift license and IOSH certification.',
    lastMessageTime: '2025-10-30 09:30',
    unread: 0,
    status: 'active'
  },
  {
    id: 13,
    candidateId: 8,
    employerId: 2,
    jobId: 6,
    lastMessage: 'I am passionate about sustainable infrastructure.',
    lastMessageTime: '2025-11-01 11:15',
    unread: 2,
    status: 'active'
  },
  {
    id: 14,
    candidateId: 26,
    employerId: 1,
    jobId: 21,
    lastMessage: 'I have my SMSTS and 12 years site experience.',
    lastMessageTime: '2025-10-29 14:20',
    unread: 0,
    status: 'active'
  },
  {
    id: 15,
    candidateId: 28,
    employerId: 5,
    jobId: 23,
    lastMessage: 'I have managed luxury hotels for 9 years.',
    lastMessageTime: '2025-10-31 15:00',
    unread: 1,
    status: 'active'
  },
  {
    id: 16,
    candidateId: 12,
    employerId: 3,
    jobId: 12,
    lastMessage: 'I hold CISSP and CEH certifications.',
    lastMessageTime: '2025-10-30 10:45',
    unread: 0,
    status: 'active'
  },
  {
    id: 17,
    candidateId: 30,
    employerId: 2,
    jobId: 25,
    lastMessage: 'I have PGCE and 6 years teaching experience.',
    lastMessageTime: '2025-10-29 16:30',
    unread: 0,
    status: 'active'
  },
  {
    id: 18,
    candidateId: 34,
    employerId: 4,
    jobId: 29,
    lastMessage: 'I am CFA Level 2 and have strong modeling skills.',
    lastMessageTime: '2025-10-31 09:00',
    unread: 1,
    status: 'active'
  },
  {
    id: 19,
    candidateId: 22,
    employerId: 1,
    jobId: 3,
    lastMessage: 'I specialize in traditional upholstery techniques.',
    lastMessageTime: '2025-10-30 13:20',
    unread: 0,
    status: 'active'
  },
  {
    id: 20,
    candidateId: 37,
    employerId: 3,
    jobId: 32,
    lastMessage: 'I have delivered successful SEO campaigns for 6 years.',
    lastMessageTime: '2025-11-01 10:30',
    unread: 2,
    status: 'active'
  },
  {
    id: 21,
    candidateId: 45,
    employerId: 3,
    jobId: 9,
    lastMessage: 'I have AWS and Azure certifications.',
    lastMessageTime: '2025-10-29 15:45',
    unread: 0,
    status: 'active'
  },
  {
    id: 22,
    candidateId: 52,
    employerId: 4,
    jobId: 15,
    lastMessage: 'I have 10 years Class 1 experience.',
    lastMessageTime: '2025-10-31 08:00',
    unread: 1,
    status: 'active'
  },
  {
    id: 23,
    candidateId: 15,
    employerId: 5,
    jobId: 18,
    lastMessage: 'I have current NMC registration and A&E experience.',
    lastMessageTime: '2025-10-30 12:15',
    unread: 0,
    status: 'active'
  },
  {
    id: 24,
    candidateId: 42,
    employerId: 2,
    jobId: 27,
    lastMessage: 'I program 5-axis CNC machines daily.',
    lastMessageTime: '2025-10-29 11:30',
    unread: 0,
    status: 'active'
  },
  {
    id: 25,
    candidateId: 41,
    employerId: 1,
    jobId: 35,
    lastMessage: 'I am Level 3 qualified with 4 years experience.',
    lastMessageTime: '2025-11-01 13:00',
    unread: 1,
    status: 'active'
  },
];

// MESSAGES DATA (Sample messages for conversations)
export const messages: Message[] = [
  // Conversation 1
  {
    id: 1,
    conversationId: 1,
    senderId: 5,
    receiverId: 13,
    content: 'Thank you for your application. We would like to invite you for an interview this Friday at 10am. Does this suit you?',
    timestamp: '2025-11-01 10:00',
    read: true
  },
  {
    id: 2,
    conversationId: 1,
    senderId: 13,
    receiverId: 5,
    content: 'Yes, that works perfectly for me! Thank you for the opportunity.',
    timestamp: '2025-11-01 10:15',
    read: true
  },
  {
    id: 3,
    conversationId: 1,
    senderId: 5,
    receiverId: 13,
    content: 'Great! The interview will be at our Stevenage office. Please bring your qualifications and two references.',
    timestamp: '2025-11-01 14:00',
    read: true
  },
  {
    id: 4,
    conversationId: 1,
    senderId: 13,
    receiverId: 5,
    content: 'Thank you! I look forward to the interview on Friday.',
    timestamp: '2025-11-01 14:30',
    read: true
  },
  // Conversation 2
  {
    id: 5,
    conversationId: 2,
    senderId: 9,
    receiverId: 3,
    content: 'Hi, I am very interested in the Full Stack Developer position. Could you tell me more about the tech stack you use?',
    timestamp: '2025-11-01 15:00',
    read: true
  },
  {
    id: 6,
    conversationId: 2,
    senderId: 3,
    receiverId: 9,
    content: 'Hello Rachel! We primarily use React on the frontend, Node.js backend, and AWS for infrastructure. We also work with TypeScript and MongoDB.',
    timestamp: '2025-11-01 16:30',
    read: true
  },
  {
    id: 7,
    conversationId: 2,
    senderId: 9,
    receiverId: 3,
    content: 'Could you tell me more about the tech stack?',
    timestamp: '2025-11-01 16:45',
    read: false
  },
  // Conversation 3
  {
    id: 8,
    conversationId: 3,
    senderId: 2,
    receiverId: 6,
    content: 'Your CV looks impressive. Can you tell us about your CAD experience?',
    timestamp: '2025-10-31 10:00',
    read: true
  },
  {
    id: 9,
    conversationId: 3,
    senderId: 6,
    receiverId: 2,
    content: 'I have 8 years of CAD experience with SolidWorks.',
    timestamp: '2025-10-31 11:20',
    read: true
  },
  // Conversation 4
  {
    id: 10,
    conversationId: 4,
    senderId: 4,
    receiverId: 16,
    content: 'We have an urgent need for delivery drivers. How soon can you start?',
    timestamp: '2025-11-01 08:00',
    read: true
  },
  {
    id: 11,
    conversationId: 4,
    senderId: 16,
    receiverId: 4,
    content: 'Yes, I can start immediately.',
    timestamp: '2025-11-01 09:15',
    read: false
  },
  {
    id: 12,
    conversationId: 4,
    senderId: 4,
    receiverId: 16,
    content: 'Excellent! Can you come in for an induction on Wednesday?',
    timestamp: '2025-11-01 09:30',
    read: false
  },
];

// Helper functions to get related data
export const getCompanyById = (id: number): Company | undefined => {
  return companies.find(c => c.id === id);
};

export const getUserById = (id: number): User | undefined => {
  return users.find(u => u.id === id);
};

export const getJobById = (id: number): Job | undefined => {
  return jobs.find(j => j.id === id);
};

export const getJobsByCompany = (companyId: number): Job[] => {
  return jobs.filter(j => j.companyId === companyId);
};

export const getUsersByCompany = (companyId: number): User[] => {
  return users.filter(u => u.type === 'Employer' && u.companyId === companyId);
};

export const getConversationsByCandidate = (candidateId: number): Conversation[] => {
  return conversations.filter(c => c.candidateId === candidateId);
};

export const getConversationsByEmployer = (employerId: number): Conversation[] => {
  return conversations.filter(c => c.employerId === employerId);
};

export const getMessagesByConversation = (conversationId: number): Message[] => {
  return messages.filter(m => m.conversationId === conversationId);
};

export const getCandidates = (): User[] => {
  return users.filter(u => u.type === 'Candidate');
};

export const getEmployers = (): User[] => {
  return users.filter(u => u.type === 'Employer');
};
